import { useState } from 'react';
import useGetEffectivePortfolioQuery from '@/api/queries/useGetEffectivePortfolioQuery';
import useGetProductListQuery from '@/api/queries/useGetProductListQuery';
import Button from '@/components/Button';
import { EffectivePortfolioSource } from '@/models/ProductList';
import Breadcrumbs from '@/modules/product/components/Breadcrumbs';
import EffectiveProductListSummary from '@/modules/product/components/EffectiveProductListSummary';
import ProductListDetailHeader from '@/modules/product/components/ProductListDetailHeader';
import ProductListErrorState from '@/modules/product/components/ProductListErrorState';
import ProductListLoadingState from '@/modules/product/components/ProductListLoadingState';
import ProductListPreviewStoreSelect, {
  PreviewStoreItem,
} from '@/modules/product/components/ProductListPreviewStoreSelect';
import ProductListUnavailableState from '@/modules/product/components/ProductListUnavailableState';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';

const sourceDescriptions = {
  [EffectivePortfolioSource.StoreAssignment]:
    'This store uses the lists below instead of its retailer’s lists.',
  [EffectivePortfolioSource.RetailerAssignment]: 'This store uses its retailer’s lists.',
  [EffectivePortfolioSource.Unrestricted]: 'This store isn’t limited to specific product lists.',
  [EffectivePortfolioSource.SupplierUnavailable]: 'This supplier is unavailable for this store.',
  [EffectivePortfolioSource.Unknown]: 'We couldn’t check this store’s lists. Try refreshing.',
};

const ProductListPreview = () => {
  const { productListId } = useParams();
  const { productList, isLoading, error } = useGetProductListQuery(productListId);
  const [selectedStore, setSelectedStore] = useState<PreviewStoreItem | null>(null);
  // Supplier scope is derived by the backend for supplier admins.
  const preview = useGetEffectivePortfolioQuery(
    selectedStore?.id,
    undefined,
    Boolean(productList && !error),
  );

  if (isLoading) return <ProductListLoadingState label="Loading Product List" />;
  if (error || !productList) return <ProductListUnavailableState />;

  const portfolio = preview.portfolio;
  const resolution = portfolio
    ? (sourceDescriptions[portfolio.source] ?? sourceDescriptions.UNKNOWN)
    : undefined;
  const hasAssignments =
    portfolio?.source === EffectivePortfolioSource.StoreAssignment ||
    portfolio?.source === EffectivePortfolioSource.RetailerAssignment;

  return (
    <div>
      <Breadcrumbs />
      <ProductListDetailHeader productList={productList} />
      <h2 className="text-lg font-semibold text-gray-900">Which lists apply to a store?</h2>
      <p className="mt-2 mb-6 text-sm text-gray-600">
        Select a store to see all the Product Lists that apply to it.
      </p>
      <div className="mb-6 max-w-lg">
        <ProductListPreviewStoreSelect
          supplierId={productList.supplierId}
          value={selectedStore}
          onSelect={(item) => setSelectedStore(item ?? null)}
        />
      </div>

      {selectedStore && (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-semibold text-gray-900">
              Product Lists for {selectedStore.store.name}
            </h3>
            <Button
              variant="plain"
              size="xs"
              onClick={() => void preview.refetch()}
              loading={preview.isFetching}
              icon={<ArrowPathIcon className="h-5 w-5" aria-hidden="true" />}
            >
              Refresh
            </Button>
          </div>
          {preview.isLoading ? (
            <ProductListLoadingState label="Checking store lists" />
          ) : preview.error || !portfolio || !resolution ? (
            <ProductListErrorState
              message="Unable to check this store’s lists. Please try again."
              onRetry={() => void preview.refetch()}
            />
          ) : (
            <div aria-live="polite">
              <p className="mb-3 text-sm text-gray-600">{resolution}</p>
              {hasAssignments && (
                <>
                  {portfolio.productListIds.length > 0 ? (
                    <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200">
                      {Array.from(new Set(portfolio.productListIds)).map((id) => (
                        <EffectiveProductListSummary key={id} productListId={id} />
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-600">
                      No lists were found for this assignment. Try refreshing.
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductListPreview;
